import React from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import EditButton from "../../../backend/edit-button";
import Image from "../../image/image";
import TimetableMetaInfos from "@default/teasers/appearance/components/timetable-meta-infos";
import TimetableAccessibilityInfos from "@default/teasers/appearance/components/timetable-accessibility-infos";
import CustomHyphenation from "./../../custom-hyphenation";
import classNames from "classnames";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import { isPromoted } from "@default/lib/is-promoted";
import { FormattedMessage } from "react-intl";

const Title = ({ typeDependingContent, item }) => {
  const isMobile = useSelector((reduxStore) => reduxStore.appStore.isMobile);

  return (
    <>
      {item?.fieldMainPresenter && (
        <h5 className="subline body-m make-headline-light d-flex flex-wrap">
          <div className="label">
            <FormattedMessage id="presenter.main" />
            :&nbsp;
          </div>
          {item?.fieldMainPresenter}
        </h5>
      )}
      {typeDependingContent?.fieldSubline && (
        <h5 className="subline body-m make-headline-light">
          {typeDependingContent.fieldSubline}
        </h5>
      )}
      {typeDependingContent?.title && (
        <h4 className="name headline-m">
          <CustomHyphenation length={15}>
            {typeDependingContent.title}
          </CustomHyphenation>
        </h4>
      )}
      {isMobile && (
        <TimetableAccessibilityInfos content={item} color={"black"} />
      )}
    </>
  );
};

const TeaserAppearanceTimetable = ({ item, index }) => {
  const adminApp = useSelector((reduxStore) => reduxStore.appStore.adminApp);
  const { simpleDesign } = useAccessibilityContext();

  {
    /*
     * If the teaser is rendering an Brand-based Content (eg: Conference Content or Arts & Words)
     * We have every content inside of the event itself.
     * If the teaser is rendering a Concert, we do not want to show the Events Title, Image and more.
     * In this case we want to render these infos directly from the artist.
     */
  }

  const location = useLocation();

  let type =
      item.fieldHappening?.fieldFormat &&
      item.fieldHappening &&
      item.fieldHappening.fieldFormat &&
      item.fieldHappening.fieldFormat[0] &&
      item.fieldHappening.fieldFormat[0].name
        ? item.fieldHappening?.fieldFormat[0].name
        : item.fieldHappening?.fieldParticipantCategory?.name,
    typeDependingContent = item.fieldHappening;

  if (
    item.fieldHappening?.fieldEventType?.name === "Arts & Word" &&
    item.fieldHappening?.fieldParticipantCategory?.name
  ) {
    type = item.fieldHappening?.fieldParticipantCategory?.name;
  } else if (item.fieldHappening?.fieldEventType?.name) {
    type = item.fieldHappening?.fieldEventType?.name;
  }

  const showImage =
    !!typeDependingContent?.fieldImage?.fieldMediaImage &&
    (!isPromoted(typeDependingContent) ||
      !!typeDependingContent?.fieldSponsoredCroppedImage);

  const teaserClassNames = classNames({
    "node node-teaser teaser-event masked timetable col-12": true,
    "with-image": showImage,
  });

  if (!typeDependingContent) return;

  return (
    <Link
      className={teaserClassNames}
      data-index={index}
      data-entity-id={item.id}
      to={typeDependingContent?.uri?.path || typeDependingContent?.path.alias}
      aria-label={`Teaser: ${item.title}`}
    >
      <EditButton
        adminApp={adminApp}
        entityId={item.id}
        destinationRoute={location.pathname}
      />
      <div className="inner row">
        <div
          className={classNames({
            "col-12 meta-infos": true,
            "col-lg-8": simpleDesign && showImage,
            "col-lg-3 col-xl-2": !simpleDesign,
          })}
        >
          <TimetableMetaInfos content={item} type={type} />
          {simpleDesign && (
            <Title typeDependingContent={typeDependingContent} item={item} />
          )}
        </div>

        {!simpleDesign && (
          <div className="col-12 col-lg-7 col-xl-8 title">
            <Title typeDependingContent={typeDependingContent} item={item} />
          </div>
        )}

        {showImage && (
          <div className="col-12 col-lg-2 image">
            <div className="mask-wrapper">
              <Image
                data={typeDependingContent?.fieldImage.fieldMediaImage}
                credit={typeDependingContent?.fieldImage.fieldCredits}
                nodeTitle={typeDependingContent?.title}
              />
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};

export const TeaserEventTimetablePropTypes = PropTypes.shape({
  id: PropTypes.string,
  title: PropTypes.string,
  path: PropTypes.shape({
    alias: PropTypes.string,
  }),
  url: PropTypes.shape({
    path: PropTypes.string,
  }),
  fieldTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    })
  ),
  fieldImage: PropTypes.shape({
    fieldMediaImage: PropTypes.shape({
      alt: PropTypes.string,
      title: PropTypes.string,
      style: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
  }),
});

TeaserAppearanceTimetable.propTypes = {
  item: TeaserEventTimetablePropTypes,
};

export default TeaserAppearanceTimetable;
